import { useEffect, useMemo, useState } from "react";
import {
  BarChart3,
  BookOpen,
  Lightbulb,
  Layers,
  TrendingUp,
  AlertTriangle,
  X,
  RotateCcw,
  ChevronRight,
  Filter,
} from "lucide-react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  LineChart,
  Line,
  Legend,
} from "recharts";
import { fetchCatalog, type CatalogData } from "../api/graph";
import { useGraphToken } from "../auth/useGraphToken";
import type { CatalogItem } from "../types/navigator";
import { ItemModal } from "../components/ItemModal";
import { statusCategory } from "../components/Badge";
import { sensitivityInfo } from "../utils/platformSensitivity";


const COLORS = [
  "#3b82f6",
  "#22c55e",
  "#a855f7",
  "#f59e0b",
  "#ec4899",
  "#14b8a6",
  "#ef4444",
  "#64748b",
];


const KIND_COLORS: Record<string, string> = {
  library: "#3b82f6",
  initiative: "#a855f7",
  platform: "#14b8a6",
};


type ChartDatum = { name: string; value: number };


type FilterField =
  | "libraryStatus"
  | "libraryAssetType"
  | "libraryDepartment"
  | "initiativeStatus"
  | "initiativePriority"
  | "initiativeTeam"
  | "platformStatus"
  | "platformType";


type Filters = Partial<Record<FilterField, string>>;


const FILTER_LABELS: Record<FilterField, string> = {
  libraryStatus: "Library status",
  libraryAssetType: "Asset type",
  libraryDepartment: "Department",
  initiativeStatus: "Initiative status",
  initiativePriority: "Priority",
  initiativeTeam: "Team / region",
  platformStatus: "Platform status",
  platformType: "Platform type",
};


type DrillRequest = {
  title: string;
  subtitle?: string;
  items: CatalogItem[];
  applyFilter?: { field: FilterField; value: string; active: boolean };
};


function bucket(s: string | undefined | null): string {
  return (s ?? "").trim() || "Unspecified";
}


function groupBy(
  items: CatalogItem[],
  pick: (i: CatalogItem) => string
): ChartDatum[] {
  const map = new Map<string, number>();
  for (const it of items) {
    const k = bucket(pick(it));
    map.set(k, (map.get(k) ?? 0) + 1);
  }
  return Array.from(map.entries())
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);
}


function itemsInBucket(
  items: CatalogItem[],
  pick: (i: CatalogItem) => string,
  value: string
): CatalogItem[] {
  return items.filter((i) => bucket(pick(i)) === value);
}


// Delegates to Badge's statusCategory — the same classification that
// drives status badge colors everywhere else, so "approved"/"pending"
// here can't drift out of sync with what the UI actually shows.
function isApproved(s: string): boolean {
  return statusCategory(s) === "approved";
}


function isPending(s: string): boolean {
  return statusCategory(s) === "pending";
}


function daysSince(iso?: string): number | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
}


function applyFilters(data: CatalogData, f: Filters): CatalogData {
  const matches = (val: string | undefined, target: string | undefined) =>
    target == null || bucket(val) === target;


  const library = data.library.filter((i) => {
    if (i.kind !== "library") return true;
    return (
      matches(i.status, f.libraryStatus) &&
      matches(i.assetType, f.libraryAssetType) &&
      matches(i.department, f.libraryDepartment)
    );
  });


  const initiatives = data.initiatives.filter((i) => {
    if (i.kind !== "initiative") return true;
    return (
      matches(i.status, f.initiativeStatus) &&
      matches(i.priority, f.initiativePriority) &&
      matches(i.team || i.region, f.initiativeTeam)
    );
  });


  const platforms = data.platforms.filter((i) => {
    if (i.kind !== "platform") return true;
    return (
      matches(i.status, f.platformStatus) &&
      matches(i.platformType, f.platformType)
    );
  });


  const all = [...library, ...initiatives, ...platforms] as CatalogData["all"];


  return { library, initiatives, platforms, all };
}


function hasAnyFilter(f: Filters): boolean {
  return Object.values(f).some((v) => v != null);
}


function itemKindLabel(kind: CatalogItem["kind"]): string {
  if (kind === "library") return "Library";
  if (kind === "initiative") return "Initiative";
  return "Platform";
}


function itemSecondary(item: CatalogItem): string {
  if (item.kind === "library") {
    return [item.assetType, item.department].filter(Boolean).join(" · ");
  }
  if (item.kind === "initiative") {
    return [item.team || item.region, item.priority].filter(Boolean).join(" · ");
  }
  return [item.platformType, item.licenseType].filter(Boolean).join(" · ");
}


function StatBlock({
  label,
  value,
  hint,
  onClick,
  highlight,
}: {
  label: string;
  value: string | number;
  hint?: string;
  onClick?: () => void;
  highlight?: boolean;
}) {
  const interactive = !!onClick;
  return (
    <div
      className="insight-stat"
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      style={{
        cursor: interactive ? "pointer" : "default",
        position: "relative",
        outline: "none",
        borderRadius: 10,
        transition: "background 140ms ease, transform 140ms ease",
        ...(highlight
          ? { background: "var(--accent-soft, var(--surface-strong))" }
          : null),
      }}
      onMouseEnter={
        interactive
          ? (e) => {
              (e.currentTarget as HTMLDivElement).style.background =
                "var(--surface-strong)";
            }
          : undefined
      }
      onMouseLeave={
        interactive
          ? (e) => {
              (e.currentTarget as HTMLDivElement).style.background = highlight
                ? "var(--accent-soft, var(--surface-strong))"
                : "transparent";
            }
          : undefined
      }
      title={interactive ? "Click to see the items" : undefined}
    >
      <div className="insight-stat-value">{value}</div>
      <div
        className="insight-stat-label"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        {label}
        {interactive ? (
          <ChevronRight
            size={12}
            style={{ opacity: 0.55 }}
            aria-hidden
          />
        ) : null}
      </div>
      {hint ? <div className="insight-stat-hint">{hint}</div> : null}
    </div>
  );
}


function DonutCard({
  title,
  data,
  activeValue,
  onSelect,
}: {
  title: string;
  data: ChartDatum[];
  activeValue?: string;
  onSelect?: (value: string) => void;
}) {
  const total = data.reduce((s, d) => s + d.value, 0);
  const top = data.slice(0, 6);
  const interactive = !!onSelect;
  return (
    <div className="insight-card">
      <div className="insight-card-title">{title}</div>
      {total === 0 ? (
        <div className="insight-empty">No data yet</div>
      ) : (
        <>
          <div className="insight-chart">
            <ResponsiveContainer width="100%" height={160}>
              <PieChart>
                <Pie
                  data={top}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={40}
                  outerRadius={66}
                  paddingAngle={2}
                  stroke="none"
                  onClick={
                    interactive
                      ? (p: { name?: string }) =>
                          p?.name && onSelect?.(p.name)
                      : undefined
                  }
                  style={{ cursor: interactive ? "pointer" : "default" }}
                >
                  {top.map((d, i) => {
                    const isActive = activeValue === d.name;
                    const isDimmed = !!activeValue && !isActive;
                    return (
                      <Cell
                        key={i}
                        fill={COLORS[i % COLORS.length]}
                        fillOpacity={isDimmed ? 0.3 : 1}
                        stroke={isActive ? "var(--text, #111)" : "none"}
                        strokeWidth={isActive ? 2 : 0}
                      />
                    );
                  })}
                </Pie>
                <Tooltip
                  contentStyle={{
                    background: "var(--surface-2)",
                    border: "1px solid var(--border)",
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <ul className="insight-legend">
            {top.map((d, i) => {
              const isActive = activeValue === d.name;
              const isDimmed = !!activeValue && !isActive;
              return (
                <li
                  key={d.name}
                  onClick={
                    interactive ? () => onSelect?.(d.name) : undefined
                  }
                  style={{
                    cursor: interactive ? "pointer" : "default",
                    opacity: isDimmed ? 0.55 : 1,
                    fontWeight: isActive ? 600 : undefined,
                  }}
                >
                  <span
                    className="insight-legend-swatch"
                    style={{ background: COLORS[i % COLORS.length] }}
                  />
                  <span className="insight-legend-label">{d.name}</span>
                  <span className="insight-legend-value">{d.value}</span>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}


function BarCard({
  title,
  data,
  color,
  activeValue,
  onSelect,
}: {
  title: string;
  data: ChartDatum[];
  color?: string;
  activeValue?: string;
  onSelect?: (value: string) => void;
}) {
  const top = data.slice(0, 6);
  const interactive = !!onSelect;
  // Start the palette at the chart's "kind" color if it's one of ours, so
  // the Initiative chart leads with purple, the Library chart with blue,
  // etc. Subsequent bars cycle through the rest of the palette — same
  // behavior as PieCard so each category gets its own color.
  const paletteStart = color ? Math.max(0, COLORS.indexOf(color)) : 0;
  const cellColor = (i: number) =>
    COLORS[(paletteStart + i) % COLORS.length];
  return (
    <div className="insight-card">
      <div className="insight-card-title">{title}</div>
      {top.length === 0 ? (
        <div className="insight-empty">No data yet</div>
      ) : (
        <div className="insight-chart">
          <ResponsiveContainer width="100%" height={180}>
            <BarChart
              data={top}
              layout="vertical"
              margin={{ left: 8, right: 8 }}
            >
              <CartesianGrid horizontal={false} stroke="var(--border)" />
              <XAxis type="number" hide />
              <YAxis
                type="category"
                dataKey="name"
                width={110}
                tick={{ fontSize: 11, fill: "var(--text-muted)" }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                contentStyle={{
                  background: "var(--surface-2)",
                  border: "1px solid var(--border)",
                  borderRadius: 8,
                  fontSize: 12,
                }}
              />
              <Bar
                dataKey="value"
                radius={[0, 4, 4, 0]}
                onClick={
                  interactive
                    ? (p: { name?: string }) =>
                        p?.name && onSelect?.(p.name)
                    : undefined
                }
                style={{ cursor: interactive ? "pointer" : "default" }}
              >
                {top.map((d, i) => {
                  const isActive = activeValue === d.name;
                  const isDimmed = !!activeValue && !isActive;
                  return (
                    <Cell
                      key={i}
                      fill={cellColor(i)}
                      fillOpacity={isDimmed ? 0.35 : 1}
                      stroke={isActive ? "var(--text, #111)" : "none"}
                      strokeWidth={isActive ? 1.5 : 0}
                    />
                  );
                })}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}


type TrendRow = {
  month: string;
  library: number;
  initiative: number;
  platform: number;
};


function buildTrend(catalog: CatalogData): TrendRow[] {
  const months: { key: string; label: string; date: Date }[] = [];
  const now = new Date();
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({
      key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
      label: d.toLocaleDateString(undefined, { month: "short" }),
      date: d,
    });
  }


  const rows: TrendRow[] = months.map((m) => ({
    month: m.label,
    library: 0,
    initiative: 0,
    platform: 0,
  }));


  const addToBucket = (created: string | undefined, kind: keyof TrendRow) => {
    if (!created) return;
    const d = new Date(created);
    if (Number.isNaN(d.getTime())) return;
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const idx = months.findIndex((m) => m.key === key);
    if (idx === -1) return;
    (rows[idx][kind] as number) += 1;
  };


  catalog.library.forEach((i) => addToBucket(i.created, "library"));
  catalog.initiatives.forEach((i) => addToBucket(i.created, "initiative"));
  catalog.platforms.forEach((i) => addToBucket(i.created, "platform"));


  return rows;
}


function TrendCard({ data }: { data: TrendRow[] }) {
  const total = data.reduce(
    (s, r) => s + r.library + r.initiative + r.platform,
    0
  );
  return (
    <div className="insight-card insight-card-wide">
      <div className="insight-card-title">
        Activity over the last 6 months
      </div>
      {total === 0 ? (
        <div className="insight-empty">No activity in the last 6 months</div>
      ) : (
        <div className="insight-chart">
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={data} margin={{ left: -16, right: 8, top: 8 }}>
              <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 11, fill: "var(--text-muted)" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fontSize: 11, fill: "var(--text-muted)" }}
                axisLine={false}
                tickLine={false}
                allowDecimals={false}
              />
              <Tooltip
                contentStyle={{
                  background: "var(--surface-2)",
                  border: "1px solid var(--border)",
                  borderRadius: 8,
                  fontSize: 12,
                }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} iconType="circle" />
              <Line
                type="monotone"
                dataKey="library"
                name="Library"
                stroke={KIND_COLORS.library}
                strokeWidth={2}
                dot={{ r: 3 }}
              />
              <Line
                type="monotone"
                dataKey="initiative"
                name="Initiatives"
                stroke={KIND_COLORS.initiative}
                strokeWidth={2}
                dot={{ r: 3 }}
              />
              <Line
                type="monotone"
                dataKey="platform"
                name="Platforms"
                stroke={KIND_COLORS.platform}
                strokeWidth={2}
                dot={{ r: 3 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}


function SectionHead({
  icon: Icon,
  title,
  subtitle,
}: {
  icon: typeof BookOpen;
  title: string;
  subtitle: string;
}) {
  return (
    <div className="dash-section-head">
      <h2 className="dash-section-title">
        <Icon size={16} /> {title}
      </h2>
      <p className="dash-section-sub">{subtitle}</p>
    </div>
  );
}


function ResetFiltersButton({
  disabled,
  onClick,
}: {
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "6px 12px",
        background: disabled ? "transparent" : "var(--surface-strong)",
        border: "1px solid var(--border-strong)",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 600,
        cursor: disabled ? "not-allowed" : "pointer",
        color: disabled ? "var(--text-muted)" : "var(--text)",
        opacity: disabled ? 0.5 : 1,
      }}
      title={disabled ? "No filters applied" : "Clear all filters"}
    >
      <RotateCcw size={12} />
      Reset filters
    </button>
  );
}


function FilterBar({
  filters,
  onClear,
  onResetAll,
}: {
  filters: Filters;
  onClear: (field: FilterField) => void;
  onResetAll: () => void;
}) {
  const entries = Object.entries(filters).filter(([, v]) => v != null) as [
    FilterField,
    string,
  ][];
  if (entries.length === 0) return null;
  return (
    <>
      {/* Spacer reserves layout height since the bar is taken out of flow. */}
      <div aria-hidden style={{ height: 52, margin: "0 0 16px" }} />
      <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 8,
        alignItems: "center",
        padding: "10px 12px",
        background: "var(--surface-strong)",
        border: "1px solid var(--border-strong)",
        borderRadius: 10,
        fontSize: 12,
        position: "fixed",
        top: 16,
        left: "50%",
        transform: "translateX(-50%)",
        width: "min(calc(100vw - 56px), 1384px)",
        zIndex: 50,
        backdropFilter: "saturate(140%) blur(8px)",
        WebkitBackdropFilter: "saturate(140%) blur(8px)",
        boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
      }}
    >
      <Filter size={13} style={{ color: "var(--text-muted)" }} aria-hidden />
      <span style={{ color: "var(--text-muted)", fontWeight: 600 }}>
        Filtering by:
      </span>
      {entries.map(([key, value]) => (
        <button
          key={key}
          type="button"
          onClick={() => onClear(key)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "4px 8px 4px 10px",
            background: "var(--surface-1)",
            border: "1px solid var(--border)",
            borderRadius: 999,
            fontSize: 12,
            cursor: "pointer",
            color: "var(--text)",
          }}
          title="Click to remove this filter"
        >
          <span style={{ color: "var(--text-muted)" }}>
            {FILTER_LABELS[key]}:
          </span>
          <strong>{value}</strong>
          <X size={12} />
        </button>
      ))}
      <div style={{ marginLeft: "auto" }}>
        <button
          type="button"
          onClick={onResetAll}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "6px 12px",
            background: "transparent",
            border: "1px solid var(--border-strong)",
            borderRadius: 999,
            fontSize: 12,
            fontWeight: 600,
            cursor: "pointer",
            color: "var(--text)",
          }}
          title="Clear all filters"
        >
          <RotateCcw size={12} />
          Reset filters
        </button>
      </div>
    </div>
    </>
  );
}


function DrillDownModal({
  request,
  onClose,
  onApplyFilter,
  onSelectItem,
}: {
  request: DrillRequest;
  onClose: () => void;
  onApplyFilter: () => void;
  onSelectItem: (item: CatalogItem) => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);


  const af = request.applyFilter;


  return (
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="drill-title"
      onClick={onClose}
    >
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 640 }}
      >
        <div className="modal-head">
          <div className="modal-head-text">
            <span className="modal-kind">
              {request.items.length}{" "}
              {request.items.length === 1 ? "item" : "items"}
            </span>
            <h3 id="drill-title" className="modal-title">
              {request.title}
            </h3>
            {request.subtitle ? (
              <div
                style={{
                  color: "var(--text-muted)",
                  fontSize: 13,
                  marginTop: 2,
                }}
              >
                {request.subtitle}
              </div>
            ) : null}
          </div>
          <button
            className="modal-close"
            onClick={onClose}
            aria-label="Close"
            type="button"
          >
            <X size={18} />
          </button>
        </div>


        <div
          className="modal-body"
          style={{ padding: "8px 0", maxHeight: "60vh", overflowY: "auto" }}
        >
          {request.items.length === 0 ? (
            <div
              style={{
                padding: "32px 20px",
                textAlign: "center",
                color: "var(--text-muted)",
              }}
            >
              No items in this bucket.
            </div>
          ) : (
            <ul
              style={{
                listStyle: "none",
                padding: 0,
                margin: 0,
                display: "flex",
                flexDirection: "column",
                gap: 4,
              }}
            >
              {request.items.map((item) => {
                const secondary = itemSecondary(item);
                const statusText =
                  item.kind === "library" ||
                  item.kind === "initiative" ||
                  item.kind === "platform"
                    ? item.status
                    : undefined;
                return (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() => onSelectItem(item)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        width: "100%",
                        padding: "10px 14px",
                        background: "transparent",
                        border: "1px solid transparent",
                        borderRadius: 8,
                        cursor: "pointer",
                        textAlign: "left",
                        color: "var(--text)",
                        transition:
                          "background 140ms ease, border-color 140ms ease",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background =
                          "var(--surface-strong)";
                        e.currentTarget.style.borderColor = "var(--border)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "transparent";
                        e.currentTarget.style.borderColor = "transparent";
                      }}
                    >
                      <span
                        aria-hidden
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: 999,
                          background:
                            KIND_COLORS[item.kind] ?? "var(--text-muted)",
                          flexShrink: 0,
                        }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontWeight: 600,
                            fontSize: 14,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {item.title}
                        </div>
                        <div
                          style={{
                            color: "var(--text-muted)",
                            fontSize: 12,
                            marginTop: 2,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {itemKindLabel(item.kind)}
                          {secondary ? ` · ${secondary}` : ""}
                          {statusText ? ` · ${statusText}` : ""}
                        </div>
                      </div>
                      <ChevronRight
                        size={14}
                        style={{ color: "var(--text-muted)", flexShrink: 0 }}
                        aria-hidden
                      />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>


        <div className="modal-foot">
          {af ? (
            <button
              type="button"
              className="btn"
              onClick={onApplyFilter}
              title={
                af.active
                  ? "Remove this filter from the page"
                  : "Filter the whole Insights page by this value"
              }
            >
              <Filter size={13} aria-hidden />
              {af.active ? "Remove filter" : "Filter page by this"}
            </button>
          ) : null}
          <button className="btn btn-primary" onClick={onClose} type="button">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}


export function InsightsPage() {
  const getToken = useGraphToken();
  const [data, setData] = useState<CatalogData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<Filters>({});
  const [drilldown, setDrilldown] = useState<DrillRequest | null>(null);
  const [selectedItem, setSelectedItem] = useState<CatalogItem | null>(null);


  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const catalog = await fetchCatalog(getToken);
        if (cancelled) return;
        setData(catalog);
        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken]);


  const toggleFilter = (field: FilterField, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [field]: prev[field] === value ? undefined : value,
    }));
  };
  const clearFilter = (field: FilterField) =>
    setFilters((prev) => ({ ...prev, [field]: undefined }));
  const clearAllFilters = () => setFilters({});
  const filtersActive = hasAnyFilter(filters);


  const filteredData = useMemo(
    () => (data ? applyFilters(data, filters) : null),
    [data, filters]
  );


  const portfolioStats = useMemo(() => {
    if (!filteredData) return null;
    const total =
      filteredData.library.length +
      filteredData.initiatives.length +
      filteredData.platforms.length;
    const approvedLibraryItems = filteredData.library.filter((i) =>
      isApproved(i.status)
    );
    const approvalRate =
      filteredData.library.length === 0
        ? 0
        : Math.round(
            (approvedLibraryItems.length / filteredData.library.length) * 100
          );
    return {
      total,
      library: filteredData.library.length,
      initiatives: filteredData.initiatives.length,
      platforms: filteredData.platforms.length,
      approvedLibraryItems,
      approvedLibrary: approvedLibraryItems.length,
      approvalRate,
    };
  }, [filteredData]);


  const initiativeStats = useMemo(() => {
    if (!filteredData) return null;
    const inits = filteredData.initiatives;
    const byStatus = groupBy(inits, (i) =>
      i.kind === "initiative" ? i.status : ""
    );
    const byPriority = groupBy(inits, (i) =>
      i.kind === "initiative" ? i.priority : ""
    );
    const byTeam = groupBy(inits, (i) =>
      i.kind === "initiative" ? i.team || i.region : ""
    );
    const ages = inits
      .map((i) => daysSince(i.created))
      .filter((d): d is number => d != null);
    const avgAge =
      ages.length === 0
        ? 0
        : Math.round(ages.reduce((s, d) => s + d, 0) / ages.length);
    const staleItems = inits.filter((i) => {
      const age = daysSince(i.created);
      return (
        age != null &&
        age > 60 &&
        i.kind === "initiative" &&
        isPending(i.status)
      );
    });
    const pendingItems = inits.filter(
      (i) => i.kind === "initiative" && isPending(i.status)
    );
    return {
      byStatus,
      byPriority,
      byTeam,
      avgAge,
      staleItems,
      pendingItems,
      stale: staleItems.length,
      pending: pendingItems.length,
    };
  }, [filteredData]);


  const libraryStats = useMemo(() => {
    if (!filteredData) return null;
    const lib = filteredData.library;
    const byType = groupBy(lib, (i) =>
      i.kind === "library" ? i.assetType : ""
    );
    const byStatus = groupBy(lib, (i) =>
      i.kind === "library" ? i.status : ""
    );
    const byDept = groupBy(lib, (i) =>
      i.kind === "library" ? i.department : ""
    );
    return { byType, byStatus, byDept };
  }, [filteredData]);


  const platformStats = useMemo(() => {
    if (!filteredData) return null;
    const plats = filteredData.platforms;
    const byStatus = groupBy(plats, (i) =>
      i.kind === "platform" ? i.status : ""
    );
    const byType = groupBy(plats, (i) =>
      i.kind === "platform" ? i.platformType : ""
    );
    const sensitiveItems = plats.filter(
      (i) => i.kind === "platform" && sensitivityInfo(i).key === "private"
    );
    const newest = [...plats]
      .sort((a, b) => (b.created || "").localeCompare(a.created || ""))
      .slice(0, 4);
    return {
      byStatus,
      byType,
      sensitive: sensitiveItems.length,
      sensitiveItems,
      newest,
    };
  }, [filteredData]);


  const trend = useMemo(
    () => (filteredData ? buildTrend(filteredData) : []),
    [filteredData]
  );


  const openDrill = (req: DrillRequest) => setDrilldown(req);
  const closeDrill = () => setDrilldown(null);


  const drillFromChart = (
    title: string,
    kind: "library" | "initiative" | "platform",
    field: FilterField,
    pick: (i: CatalogItem) => string,
    value: string
  ) => {
    if (!filteredData) return;
    const source =
      kind === "library"
        ? filteredData.library
        : kind === "initiative"
          ? filteredData.initiatives
          : filteredData.platforms;
    const items = itemsInBucket(source, pick, value);
    const active = filters[field] === value;
    openDrill({
      title,
      subtitle: value,
      items,
      applyFilter: { field, value, active },
    });
  };


  const handleApplyFilterFromDrill = () => {
    if (!drilldown?.applyFilter) return;
    const { field, value } = drilldown.applyFilter;
    toggleFilter(field, value);
    closeDrill();
  };


  return (
    <div className="page">
      <div className="page-hero">
        <p className="page-subtitle">
          A leadership view of AI activity across Brookfield — portfolio
          health, pipeline status, and trends across the catalog. Click any
          chart slice or KPI to drill into the items behind it.
        </p>
      </div>


      {error ? (
        <div className="error-banner" role="alert">
          <strong>Couldn't load insights.</strong> {error}
        </div>
      ) : null}


      {loading || !data || !portfolioStats ? (
        <div className="center-stage">
          <div className="spinner" />
          <p>Loading insights…</p>
        </div>
      ) : (
        <>
          <FilterBar
            filters={filters}
            onClear={clearFilter}
            onResetAll={clearAllFilters}
          />


          {filtersActive && portfolioStats.total === 0 ? (
            <div
              className="insight-card"
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 12,
                padding: "40px 24px",
                textAlign: "center",
              }}
            >
              <AlertTriangle
                size={28}
                style={{ color: "var(--text-muted)" }}
                aria-hidden
              />
              <div style={{ fontWeight: 600, fontSize: 16 }}>
                No assets match these filters
              </div>
              <p
                style={{
                  color: "var(--text-muted)",
                  fontSize: 13,
                  maxWidth: 380,
                  margin: 0,
                }}
              >
                Try removing one of the filters above, or reset everything to
                see the full catalog.
              </p>
              <ResetFiltersButton disabled={false} onClick={clearAllFilters} />
            </div>
          ) : (
            <>
              <section className="dash-section">
                <SectionHead
                  icon={BarChart3}
                  title="Portfolio at a glance"
                  subtitle="Where things stand right now across the AI catalog."
                />
                <div className="insights-grid">
                  <div className="insight-card insight-stats">
                    <div className="insight-card-title">Catalog totals</div>
                    <div className="insight-stats-row">
                      <StatBlock
                        label="Total assets"
                        value={portfolioStats.total}
                        onClick={() =>
                          openDrill({
                            title: "All catalog assets",
                            subtitle: filtersActive
                              ? "Matching current filters"
                              : undefined,
                            items: [
                              ...filteredData!.library,
                              ...filteredData!.initiatives,
                              ...filteredData!.platforms,
                            ],
                          })
                        }
                      />
                      <StatBlock
                        label="Library"
                        value={portfolioStats.library}
                        onClick={() =>
                          openDrill({
                            title: "Library assets",
                            items: filteredData!.library,
                          })
                        }
                      />
                      <StatBlock
                        label="Initiatives"
                        value={portfolioStats.initiatives}
                        onClick={() =>
                          openDrill({
                            title: "Initiatives",
                            items: filteredData!.initiatives,
                          })
                        }
                      />
                      <StatBlock
                        label="Platforms"
                        value={portfolioStats.platforms}
                        onClick={() =>
                          openDrill({
                            title: "Platforms",
                            items: filteredData!.platforms,
                          })
                        }
                      />
                    </div>
                  </div>
                  <div className="insight-card insight-stats">
                    <div className="insight-card-title">
                      Production readiness
                    </div>
                    <div className="insight-stats-row">
                      <StatBlock
                        label="In production"
                        value={portfolioStats.approvedLibrary}
                        hint="Published library assets"
                        onClick={() =>
                          openDrill({
                            title: "Library assets in production",
                            subtitle: "Published / approved / live",
                            items: portfolioStats.approvedLibraryItems,
                          })
                        }
                      />
                      <StatBlock
                        label="Approval rate"
                        value={`${portfolioStats.approvalRate}%`}
                        hint="Library published / total"
                      />
                      <StatBlock
                        label="Open initiatives"
                        value={initiativeStats?.pending ?? 0}
                        hint="Awaiting review or in progress"
                        onClick={
                          initiativeStats && initiativeStats.pending > 0
                            ? () =>
                                openDrill({
                                  title: "Open initiatives",
                                  subtitle:
                                    "Pending review, submitted, or in progress",
                                  items: initiativeStats.pendingItems,
                                })
                            : undefined
                        }
                      />
                    </div>
                  </div>
                </div>
              </section>


              <section className="dash-section">
                <SectionHead
                  icon={Lightbulb}
                  title="Initiative pipeline"
                  subtitle="How ideas are flowing through review, prioritization, and delivery."
                />
                <div className="insights-grid">
                  <div className="insight-card insight-stats">
                    <div className="insight-card-title">Pipeline health</div>
                    <div className="insight-stats-row">
                      <StatBlock
                        label="Avg age"
                        value={`${initiativeStats?.avgAge ?? 0}d`}
                        hint="Since submission"
                      />
                      <StatBlock
                        label="Stale > 60d"
                        value={initiativeStats?.stale ?? 0}
                        hint="Pending and aging"
                        onClick={
                          initiativeStats && initiativeStats.stale > 0
                            ? () =>
                                openDrill({
                                  title: "Stale initiatives (> 60d)",
                                  subtitle: "Pending review and aging",
                                  items: initiativeStats.staleItems,
                                })
                            : undefined
                        }
                      />
                    </div>
                  </div>
                  <DonutCard
                    title="By status"
                    data={initiativeStats?.byStatus ?? []}
                    activeValue={filters.initiativeStatus}
                    onSelect={(v) =>
                      drillFromChart(
                        "Initiatives by status",
                        "initiative",
                        "initiativeStatus",
                        (i) => (i.kind === "initiative" ? i.status : ""),
                        v
                      )
                    }
                  />
                  <DonutCard
                    title="By priority"
                    data={initiativeStats?.byPriority ?? []}
                    activeValue={filters.initiativePriority}
                    onSelect={(v) =>
                      drillFromChart(
                        "Initiatives by priority",
                        "initiative",
                        "initiativePriority",
                        (i) => (i.kind === "initiative" ? i.priority : ""),
                        v
                      )
                    }
                  />
                  <BarCard
                    title="Submissions by team / region"
                    data={initiativeStats?.byTeam ?? []}
                    color={KIND_COLORS.initiative}
                    activeValue={filters.initiativeTeam}
                    onSelect={(v) =>
                      drillFromChart(
                        "Initiatives by team / region",
                        "initiative",
                        "initiativeTeam",
                        (i) =>
                          i.kind === "initiative" ? i.team || i.region : "",
                        v
                      )
                    }
                  />
                </div>
              </section>


              <section className="dash-section">
                <SectionHead
                  icon={BookOpen}
                  title="AI Library composition"
                  subtitle="What's published and where it's coming from."
                />
                <div className="insights-grid">
                  <DonutCard
                    title="By asset type"
                    data={libraryStats?.byType ?? []}
                    activeValue={filters.libraryAssetType}
                    onSelect={(v) =>
                      drillFromChart(
                        "Library by asset type",
                        "library",
                        "libraryAssetType",
                        (i) => (i.kind === "library" ? i.assetType : ""),
                        v
                      )
                    }
                  />
                  <DonutCard
                    title="By status"
                    data={libraryStats?.byStatus ?? []}
                    activeValue={filters.libraryStatus}
                    onSelect={(v) =>
                      drillFromChart(
                        "Library by status",
                        "library",
                        "libraryStatus",
                        (i) => (i.kind === "library" ? i.status : ""),
                        v
                      )
                    }
                  />
                  <BarCard
                    title="By department"
                    data={libraryStats?.byDept ?? []}
                    color={KIND_COLORS.library}
                    activeValue={filters.libraryDepartment}
                    onSelect={(v) =>
                      drillFromChart(
                        "Library by department",
                        "library",
                        "libraryDepartment",
                        (i) => (i.kind === "library" ? i.department : ""),
                        v
                      )
                    }
                  />
                </div>
              </section>


              <section className="dash-section">
                <SectionHead
                  icon={Layers}
                  title="Platforms"
                  subtitle="Approval state and what's been onboarded recently."
                />
                <div className="insights-grid">
                  <div className="insight-card insight-stats">
                    <div className="insight-card-title">Platform readiness</div>
                    <div className="insight-stats-row">
                      <StatBlock
                        label="Total"
                        value={portfolioStats.platforms}
                        onClick={() =>
                          openDrill({
                            title: "All platforms",
                            items: filteredData!.platforms,
                          })
                        }
                      />
                      <StatBlock
                        label="High-risk data approved"
                        value={platformStats?.sensitive ?? 0}
                        onClick={
                          platformStats && platformStats.sensitive > 0
                            ? () =>
                                openDrill({
                                  title: "Platforms approved for high-risk data",
                                  items: platformStats.sensitiveItems,
                                })
                            : undefined
                        }
                      />
                    </div>
                  </div>
                  <DonutCard
                    title="By status"
                    data={platformStats?.byStatus ?? []}
                    activeValue={filters.platformStatus}
                    onSelect={(v) =>
                      drillFromChart(
                        "Platforms by status",
                        "platform",
                        "platformStatus",
                        (i) => (i.kind === "platform" ? i.status : ""),
                        v
                      )
                    }
                  />
                  <DonutCard
                    title="By platform type"
                    data={platformStats?.byType ?? []}
                    activeValue={filters.platformType}
                    onSelect={(v) =>
                      drillFromChart(
                        "Platforms by type",
                        "platform",
                        "platformType",
                        (i) => (i.kind === "platform" ? i.platformType : ""),
                        v
                      )
                    }
                  />
                  <div className="insight-card">
                    <div className="insight-card-title">Recently onboarded</div>
                    {(platformStats?.newest ?? []).length === 0 ? (
                      <div className="insight-empty">No data yet</div>
                    ) : (
                      <ul className="insight-list">
                        {platformStats?.newest.map((p) => (
                          <li
                            key={p.id}
                            className="insight-list-row"
                            role="button"
                            tabIndex={0}
                            onClick={() => setSelectedItem(p)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                setSelectedItem(p);
                              }
                            }}
                            style={{ cursor: "pointer" }}
                            title="Open details"
                          >
                            <span className="insight-list-title">{p.title}</span>
                            {p.kind === "platform" && p.status ? (
                              <span className="insight-list-meta">
                                {p.status}
                              </span>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </section>


              <section className="dash-section">
                <SectionHead
                  icon={TrendingUp}
                  title="Activity trend"
                  subtitle="How submissions are tracking month over month."
                />
                <div className="insights-grid insights-grid-wide">
                  <TrendCard data={trend} />
                </div>
              </section>
            </>
          )}
        </>
      )}


      {drilldown ? (
        <DrillDownModal
          request={drilldown}
          onClose={closeDrill}
          onApplyFilter={handleApplyFilterFromDrill}
          onSelectItem={(item) => {
            setSelectedItem(item);
            closeDrill();
          }}
        />
      ) : null}


      {selectedItem ? (
        <ItemModal item={selectedItem} onClose={() => setSelectedItem(null)} />
      ) : null}
    </div>
  );
}
